import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '@/components/ui/AppLayout';
import { Skeleton } from '@/components/ui/Skeleton';
import { Banner } from '@/components/ui/Banner';
import { useAuthStore } from '@/stores/auth';
import { opportunitiesApi } from '@/services/opportunities.api';

/**
 * Redirector `/opportunities/:id` → `/prospects/:prospectId?tab=opportunities&opportunity=:id`.
 *
 * Le détail d'une opportunité est désormais embarqué dans la fiche prospect
 * (onglet « Opportunités »). Cette route reste active uniquement pour la
 * rétrocompatibilité : liens des notifications (emails et centre de notifs),
 * URLs bookmarkées, résultats de recherche globale - tout ce qui référence
 * l'ancienne URL `/opportunities/:id` est redirigé.
 *
 * La lookup `opportunitiesApi.get(id)` applique déjà le scope RBAC côté
 * backend : un utilisateur hors périmètre reçoit un 404 → on le renvoie vers
 * l'accueil plutôt que vers un deep-link qui ne fonctionnerait pas.
 */
export function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    if (!id || !token) return;
    let cancelled = false;
    void (async () => {
      try {
        const opp = await opportunitiesApi.get(token, id);
        if (cancelled) return;
        navigate(`/prospects/${opp.prospect.id}?tab=opportunities&opportunity=${opp.id}`, {
          replace: true,
        });
      } catch {
        if (cancelled) return;
        navigate('/prospects', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, token, navigate]);

  return (
    <AppLayout>
      {!id ? (
        <Banner tone="danger">Identifiant d&apos;opportunité invalide.</Banner>
      ) : (
        <div className="space-y-4">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}
    </AppLayout>
  );
}
